import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { otcService } from '../services/otcService'
import { OrderbookItem, OrderbookQueryParams, OrderType, DealType, SortBy, SortDirection, OrderbookStatsResponse } from '../types/otc'
import { tradingService } from '@/services/tradingService';
import { dataRoomService } from '@/services/dataRoomService';

// Query keys for cache management
export const otcKeys = {
  all: ['otc'] as const,
  stats: () => [...otcKeys.all, 'stats'] as const,
  orders: (params?: OrderbookQueryParams) => [...otcKeys.all, 'orders', params] as const,
}

export const projectsKeys = {
  all: ['projects'] as const,
  stats: () => [...projectsKeys.all, 'stats'] as const,
}

export const useOTC = (params?: OrderbookQueryParams) => {
  const [orders, setOrders] = useState<OrderbookItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [total, setTotal] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const paramsRef = useRef(params)

  // Memoize params to create stable reference
  const stableParams = useMemo(() => params, [
    params?.limit,
    params?.offset,
    params?.search,
    params?.type,
    params?.deal_type,
    params?.industry,
    params?.sort_by,
    params?.sort_direction
  ])

  const fetchOrders = useCallback(async (page = 1, customParams?: OrderbookQueryParams) => {
    try {
      setLoading(true)
      setError(null)
      
      const activeParams = customParams || stableParams
      const limit = activeParams?.limit || 10
      const offset = (page - 1) * limit
      
      const queryParams = {
        ...activeParams,
        limit,
        offset
      }
      
      const response = await otcService.getOrderbook(queryParams)
      
      setOrders(response.data)
      setTotal(response.total)
      setCurrentPage(page)
      setTotalPages(Math.ceil(response.total / limit))
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch orders')
    } finally {
      setLoading(false)
    }
  }, [stableParams])

  useEffect(() => {
    // Only refetch if params actually changed
    const paramsChanged = JSON.stringify(paramsRef.current) !== JSON.stringify(stableParams)
    if (!paramsChanged && orders.length > 0) {
      return
    }
    
    paramsRef.current = stableParams
    fetchOrders(1)
  }, [stableParams, fetchOrders, orders.length])

  const goToPage = useCallback((page: number) => {
    if (page >= 1 && page <= totalPages && !loading) {
      fetchOrders(page)
    }
  }, [totalPages, loading, fetchOrders])

  const refresh = useCallback(() => {
    fetchOrders(currentPage)
  }, [fetchOrders, currentPage])

  return {
    orders,
    loading,
    error,
    total,
    currentPage,
    totalPages,
    goToPage,
    refresh
  }
}

// Hook for managing OTC filters with URL query params
export const useOTCFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const filters = {
    search: searchParams.get('search') || '',
    type: (searchParams.get('type') as OrderType) || '',
    assetType: (searchParams.get('assetType') as DealType) || '',
    trancheSize: searchParams.get('trancheSize') || '',
    sortBy: (searchParams.get('sortBy') as SortBy) || '',
    sortDirection: (searchParams.get('sortDirection') as SortDirection) || ''
  }

  const updateFilter = useCallback((key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams)
    if (value) {
      newParams.set(key, value)
    } else {
      newParams.delete(key)
    }
    setSearchParams(newParams)
  }, [searchParams, setSearchParams])

  const setters = {
    setSearch: (value: string) => updateFilter('search', value),
    setType: (value: OrderType | '') => updateFilter('type', value),
    setAssetType: (value: DealType | '') => updateFilter('assetType', value),
    setTrancheSize: (value: string) => updateFilter('trancheSize', value),
    setSortBy: (value: SortBy | '') => updateFilter('sortBy', value),
    setSortDirection: (value: SortDirection | '') => updateFilter('sortDirection', value),
  }

  return { filters, ...setters }
}

export * from './useOTC';

/**
 * Hook for fetching OTC orderbook stats with React Query
 * - Automatic caching (5 min stale time)
 * - Shared cache across components (Header, MainScreen, etc.)
 */
export function useOtcStats() {
  const { data, isLoading: loading, error } = useQuery({
    queryKey: otcKeys.stats(),
    queryFn: async () => {
      const response = await tradingService.getOrderbookStats();
        // API returns: { data: { total_projects, total_orderbook_value, ... } }
        // Extract data from the nested response structure
      return (response as { data?: OrderbookStatsResponse })?.data || response;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return { 
    data: data ?? null, 
    loading, 
    error: error instanceof Error ? error.message : error ? String(error) : null 
  };
}

/**
 * Hook for fetching projects stats with React Query
 * - Automatic caching (5 min stale time)
 */
export function useProjectsStats() {
  const { data, isLoading: loading, error } = useQuery({
    queryKey: projectsKeys.stats(),
    queryFn: () => dataRoomService.getProjectsStats(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return { 
    data: data ?? null, 
    loading, 
    error: error instanceof Error ? error.message : error ? String(error) : null 
  };
}
